import React, { useCallback, useEffect } from 'react';

import { useAppContext } from 'src/components/app-context';
import { usePriceRowContext } from '../../../price-row-context';

import { Direction } from '@prisjakt/ui';
import { ButtonSize, TextButton } from '@prisjakt/ui/button/variants';
import { Popover, Alignment } from '@prisjakt/ui/popover';
import { BodySmallText } from '@prisjakt/ui/text/variants';
import { IconEyeHidden } from '@prisjakt/ui/icon/eyehidden';
import { GoToStoreButton } from '../go-to-store-button';
import { StockPriceInfo } from '../stock-price-info';
import { StoreInfoColumn } from '../store-info-column';

import { trackUnfeaturedHideClick } from 'src/areas/product/components/tracking';

import { AlternativePrice } from 'src/types';
import { ITableLinkProps } from '../../types';

import {
  StyledCollapseUnfeaturedBtn,
  StyledEndColumn,
  StyledLink,
  StyledMiddleColumn,
  StyledProductName,
  StyledProductNameColumn,
  StyledTopMiddleRow,
} from './styles';

type Props = {
  alternativeOffer?: AlternativePrice;
  isAlternative?: boolean;
  priceRowRef?: React.RefObject<HTMLDivElement>;
  setShowDisabledInfo?: React.Dispatch<React.SetStateAction<boolean>>;
  showDisabledInfo?: boolean;
  tableLinkProps: ITableLinkProps;
};

export const UnfeaturedColumns: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<Props>
>> = props => {
  const {
    alternativeOffer,
    isAlternative = false,
    priceRowRef,
    setShowDisabledInfo,
    showDisabledInfo,
    tableLinkProps,
  } = props;
  const { t } = useAppContext();
  const {
    setShowUnfeaturedPlaceholder,
    showUnfeaturedHideButton,
    offer,
    productId,
  } = usePriceRowContext();

  const { priceId } = offer;

  const name = alternativeOffer?.name || offer.name;

  const handleOutSideClick = useCallback(
    (e: MouseEvent) => {
      if (
        priceRowRef &&
        priceRowRef.current &&
        priceRowRef.current.contains(e.target as HTMLElement)
      ) {
        return;
      }
      if (setShowDisabledInfo) setShowDisabledInfo(false);
    },
    [priceRowRef, setShowDisabledInfo]
  );

  // componentWillUnmount
  useEffect(() => {
    return () => {
      window.removeEventListener('click', handleOutSideClick);
    };
  }, [handleOutSideClick]);

  // Callback for showDisabledInfo state change
  useEffect(() => {
    if (!setShowDisabledInfo) return;
    if (showDisabledInfo) {
      window.addEventListener('click', handleOutSideClick);
    } else {
      window.removeEventListener('click', handleOutSideClick);
    }
  }, [handleOutSideClick, setShowDisabledInfo, showDisabledInfo]);

  const handleHideClick = () => {
    if (setShowUnfeaturedPlaceholder) setShowUnfeaturedPlaceholder();
    trackUnfeaturedHideClick(productId);
  };

  return (
    <>
      <StyledLink {...tableLinkProps} aria-describedby={`click-out-${priceId}`}>
        {!isAlternative && <StoreInfoColumn />}
        <StyledMiddleColumn>
          <StyledTopMiddleRow>
            <StyledProductNameColumn>
              <StyledProductName>{name}</StyledProductName>
            </StyledProductNameColumn>
            <StockPriceInfo alternativeOffer={alternativeOffer} />
          </StyledTopMiddleRow>
        </StyledMiddleColumn>

        <StyledEndColumn>
          <GoToStoreButton />
        </StyledEndColumn>
      </StyledLink>

      {!isAlternative && showUnfeaturedHideButton && (
        <StyledCollapseUnfeaturedBtn
          placement={Direction.Left}
          title={t('product', 'price-list.unfeatured-shops-hide-all')}
        >
          <TextButton
            ariaLabel={t('product', 'price-list.unfeatured-shops-hide-all')}
            onClick={handleHideClick}
            onlyIcon
            size={ButtonSize.SMALL}
          >
            <IconEyeHidden color="maybeBlue700" />
          </TextButton>
        </StyledCollapseUnfeaturedBtn>
      )}

      <Popover
        id={`click-out-${priceId}`}
        visible={Boolean(showDisabledInfo)}
        width={250}
        horizontalAlign={Alignment.Center}
        verticalAlign={Alignment.Center}
        onClose={() =>
          setShowDisabledInfo && setShowDisabledInfo(!showDisabledInfo)
        }
      >
        <BodySmallText>{t('product', 'transparency.store-info')}</BodySmallText>
      </Popover>
    </>
  );
};
